/**
 * The case panel's "DCF mail" section (UM-R5): every message DCF sent to this
 * case's DCF login, newest first. Silent when there is none and the ingest is
 * not switched on, so the panel does not grow a heading for a feature that is
 * not running.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { dcfMailApi, shortDateTime } from './umedApi';
import type { DcfMailPage } from './umedApi';
import { DcfMailMessage, classLine } from './DcfMailParts';
import './DcfMail.css';

export default function DcfMailCaseSection({ caseId, onChanged }: { caseId: string; onChanged?: (msg: string) => void | Promise<void> }) {
  const [data, setData] = useState<DcfMailPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const r = await dcfMailApi.list({ caseId, state: 'all' });
      if (r.success && r.data) setData(r.data); else setFailed(true);
    } catch { setFailed(true); }
  }, [caseId]);
  useEffect(() => { setData(null); setOpen(null); load(); }, [load]);

  if (failed || !data) return null;
  if (data.items.length === 0 && data.ingest === 'OFF') return null;
  return (
    <div className="um-section" aria-label="DCF mail for this case">
      <h3>DCF mail</h3>
      {data.items.length === 0
        ? <p className="um-quiet">DCF has sent nothing to this resident's DCF login.</p>
        : (
          <ul className="um-packets" aria-label="DCF mail for this case">
            {data.items.map((m) => (
              <li key={m.id}>
                <span>{shortDateTime(m.received_at)} · {classLine(m)}{m.handled ? ' · handled' : ''}</span>
                {' '}<button type="button" className="um-link" aria-expanded={open === m.id} onClick={() => setOpen(open === m.id ? null : m.id)}>{open === m.id ? 'Hide' : 'Show'}</button>
                {open === m.id && (
                  <DcfMailMessage item={m} canOpen={data.can.open && m.can_open !== false} canMark={data.can.mark_handled}
                    onChanged={async (msg) => { await load(); if (onChanged) await onChanged(msg); }} />
                )}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}
